import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Crown, Download, ExternalLink, Eye, RefreshCw } from "lucide-react";

import { API, Button, EmptyState, Modal, Pagination, SearchBar, SegmentedControl, Skeleton, apiFetch, cx } from "../../../pages/admin/_shared";
import { VIZ, fmtNum } from "../charts";
import { STATUS_META } from "../locations/workflow";

const PERIODS = [
  { value: "total", label: "Toàn thời gian" },
  { value: "d30", label: "30 ngày" },
  { value: "d7", label: "7 ngày" },
];
const PERIOD_TEXT = { total: "toàn thời gian", d30: "30 ngày gần nhất", d7: "7 ngày gần nhất" };
const PAGE_SIZE = 15;

const fmtPct = (v) => `${(Math.round(v * 10) / 10).toLocaleString("vi-VN")}%`;
const pctOf = (v, total) => (total > 0 ? (v / total) * 100 : 0);

function downloadCsv(rows, metric) {
  const head = ["Hạng", "Địa điểm", "Danh mục", "Trạng thái", "7 ngày", "30 ngày", "Tổng lượt xem", `% (${PERIOD_TEXT[metric]})`];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [r.rank, r.name, r.categoryName, STATUS_META[r.status]?.label || r.status, r.d7, r.d30, r.total, r.pct.toFixed(1)].map(esc).join(","));
  const blob = new Blob(["﻿" + [head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `luot-xem-dia-diem-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Tỷ lệ lượt xem theo danh mục — danh mục nào được người dùng quan tâm nhiều nhất. */
function CategoryShare({ cats, total, metric }) {
  if (!cats.length) return null;
  return (
    <section>
      <h3 className="text-sm font-semibold text-admin-text">Danh mục được quan tâm nhất</h3>
      <p className="mt-0.5 text-xs text-admin-textMuted">
        Tỷ lệ lượt xem theo danh mục · {PERIOD_TEXT[metric]} · {fmtNum(total)} lượt
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cats.map((c, i) => (
          <div
            key={c.name}
            className={cx(
              "rounded-xl border p-3.5",
              i === 0 && c.views > 0 ? "border-admin-primary/30 bg-admin-primaryLight" : "border-admin-border bg-admin-surface"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-admin-text">{c.name}</span>
              {i === 0 && c.views > 0 ? (
                <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-admin-primary">
                  <Crown size={12} /> Nhiều nhất
                </span>
              ) : null}
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums text-admin-text">{fmtPct(c.pct)}</span>
              <span className="text-xs text-admin-textMuted">
                {fmtNum(c.views)} lượt · {c.locations} địa điểm
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-admin-muted">
              <div className="h-full rounded-full" style={{ width: `${Math.max(1, c.pct)}%`, background: VIZ[0] }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SortHeader({ label, field, sort, onSort, className }) {
  const on = sort.field === field;
  const Icon = on ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <th className={cx("px-3 py-2.5 font-medium", className)}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cx("inline-flex items-center gap-1 transition hover:text-admin-text", on && "text-admin-text")}
      >
        {label}
        <Icon size={12} className={on ? "" : "opacity-50"} />
      </button>
    </th>
  );
}

/** Modal "Xem thêm" của thẻ "Địa điểm xem nhiều nhất": bảng lượt xem của tất cả địa điểm. */
export default function LocationViewsModal({ open, onClose }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState("total");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState({ field: "metric", dir: "desc" });
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    apiFetch(`${API}/analytics/admin/location-views`)
      .then((res) => setItems(Array.isArray(res?.items) ? res.items : []))
      .catch((e) => setError(e?.message || "Không thể tải lượt xem"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    setPage(1);
  }, [metric, search, category, sort]);

  const all = useMemo(() => items || [], [items]);
  const totalViews = useMemo(() => all.reduce((a, r) => a + (r[metric] || 0), 0), [all, metric]);

  // Hạng + % tính trên toàn bộ địa điểm (không đổi khi lọc)
  const ranked = useMemo(
    () =>
      [...all]
        .sort((a, b) => (b[metric] || 0) - (a[metric] || 0) || a.name.localeCompare(b.name, "vi"))
        .map((r, i) => ({ ...r, rank: i + 1, pct: pctOf(r[metric] || 0, totalViews) })),
    [all, metric, totalViews]
  );

  const cats = useMemo(() => {
    const m = new Map();
    all.forEach((r) => {
      const c = m.get(r.categoryName) || { name: r.categoryName, views: 0, locations: 0 };
      c.views += r[metric] || 0;
      c.locations += 1;
      m.set(r.categoryName, c);
    });
    return [...m.values()].map((c) => ({ ...c, pct: pctOf(c.views, totalViews) })).sort((a, b) => b.views - a.views);
  }, [all, metric, totalViews]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = ranked.filter((r) => (!category || r.categoryName === category) && (!q || r.name.toLowerCase().includes(q)));
    const key = sort.field === "metric" ? metric : sort.field;
    const dir = sort.dir === "asc" ? 1 : -1;
    return list.sort((a, b) =>
      key === "name" ? dir * a.name.localeCompare(b.name, "vi") : dir * ((a[key] || 0) - (b[key] || 0)) || a.rank - b.rank
    );
  }, [ranked, search, category, sort, metric]);

  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const maxPct = Math.max(...ranked.map((r) => r.pct), 1);

  const onSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: s.dir === "desc" ? "asc" : "desc" } : { field, dir: field === "name" ? "asc" : "desc" }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidthClass="max-w-6xl"
      panelClassName="max-h-[min(92vh,900px)]"
      title={
        <div>
          <h2 className="text-base font-semibold tracking-tight text-admin-text">Lượt xem theo địa điểm</h2>
          <p className="text-xs text-admin-textMuted">
            {fmtNum(all.length)} địa điểm · {fmtNum(totalViews)} lượt xem ({PERIOD_TEXT[metric]})
          </p>
        </div>
      }
      headerRight={
        <div className="hidden items-center gap-1 sm:flex">
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} aria-label="Tải lại">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button size="sm" variant="secondary" onClick={() => downloadCsv(rows, metric)} disabled={!rows.length}>
            <Download size={14} /> CSV
          </Button>
        </div>
      }
    >
      {error ? (
        <EmptyState icon={Eye} title="Không tải được dữ liệu" description={error} action={<Button onClick={load}>Thử lại</Button>} />
      ) : !items ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : (
        <div className="space-y-6">
          <SegmentedControl value={metric} onChange={setMetric} options={PERIODS} ariaLabel="Khoảng thời gian" />

          <CategoryShare cats={cats} total={totalViews} metric={metric} />

          <section className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm địa điểm..." className="sm:max-w-xs" />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-10 rounded-lg border border-admin-border bg-admin-surface px-3 text-sm text-admin-text"
                aria-label="Lọc danh mục"
              >
                <option value="">Tất cả danh mục</option>
                {cats.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="overflow-x-auto rounded-xl border border-admin-border">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-admin-muted/60 text-left text-xs text-admin-textMuted">
                  <tr>
                    <th className="w-12 px-3 py-2.5 font-medium">#</th>
                    <SortHeader label="Địa điểm" field="name" sort={sort} onSort={onSort} />
                    <th className="px-3 py-2.5 font-medium">Danh mục</th>
                    <SortHeader label="7 ngày" field="d7" sort={sort} onSort={onSort} className="text-right" />
                    <SortHeader label="30 ngày" field="d30" sort={sort} onSort={onSort} className="text-right" />
                    <SortHeader label="Tổng" field="total" sort={sort} onSort={onSort} className="text-right" />
                    <SortHeader label={`Tỷ lệ (${PERIODS.find((p) => p.value === metric).label})`} field="metric" sort={sort} onSort={onSort} className="w-48" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-border">
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-sm text-admin-textMuted">
                        Không có địa điểm phù hợp
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((r) => {
                      const st = STATUS_META[r.status];
                      return (
                        <tr key={r._id} className="hover:bg-admin-muted/40">
                          <td className="px-3 py-2.5 tabular-nums text-admin-textMuted">{r.rank}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="min-w-0 truncate font-medium text-admin-text">{r.name}</span>
                              {r.slug ? (
                                <a
                                  href={`/location/${r.slug}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="shrink-0 text-admin-textMuted hover:text-admin-primary"
                                  aria-label="Mở trang địa điểm"
                                >
                                  <ExternalLink size={13} />
                                </a>
                              ) : null}
                            </div>
                            {st && r.status !== "published" ? (
                              <span className={cx("mt-0.5 inline-flex items-center gap-1 text-[11px]", "text-admin-textMuted")}>
                                <span className={cx("h-1.5 w-1.5 rounded-full", st.dot)} />
                                {st.label}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5 text-admin-textMuted">{r.categoryName}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-admin-text">{fmtNum(r.d7)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-admin-text">{fmtNum(r.d30)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-admin-text">{fmtNum(r.total)}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-admin-muted">
                                <div className="h-full rounded-full" style={{ width: `${(r.pct / maxPct) * 100}%`, background: VIZ[0] }} />
                              </div>
                              <span className="w-12 text-right text-xs tabular-nums text-admin-text">{fmtPct(r.pct)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <Pagination page={page} pages={pages} total={rows.length} onChange={setPage} className="border-t border-admin-border" />
            </div>
            <p className="text-xs text-admin-textMuted">
              Lượt xem 7/30 ngày được ghi nhận theo ngày (giờ Việt Nam); cột "Tổng" là tổng lượt xem từ khi địa điểm được tạo.
            </p>
          </section>
        </div>
      )}
    </Modal>
  );
}
